"use client";

import Link from "next/link";
import DocLayout, { DocSection } from "../../components/DocLayout";
import { TIERS } from "../../lib/tiers";
import { CREDITS } from "../../lib/ful-config";


export default function Terms() {

  return (
    <DocLayout title="Terms of Service" subtitle="Last updated: May 2026">
      <DocSection title="What Fülkit is">
        <p>
          Fülkit is an AI-powered personal knowledge assistant. You store notes,
          connect sources, and talk to an AI that has context on everything
          you've saved. It's your second brain.
        </p>
      </DocSection>

      <DocSection title="Beta status (Cohort 1)">
        <p>
          Fülkit is currently in <strong>invite-only beta</strong>. Access is
          released in cohorts and the product is evolving rapidly. By signing
          up during the beta period, you accept the additional terms in this
          section, which apply <em>in addition to</em> the rest of these Terms.
        </p>
        <p style={{ marginTop: "var(--space-3)" }}>
          <strong>Service availability.</strong> The Fülkit beta is operational
          but should be considered pre-release. Expect bugs, breaking changes,
          and occasional downtime. The "as is" and "as available" disclaimers
          in these Terms apply with particular force during beta. No
          service-level commitment (uptime SLA, response-time SLA, or recovery
          objective) applies during the beta period. We aim for high
          reliability but cannot guarantee it.
        </p>
        <p style={{ marginTop: "var(--space-3)" }}>
          <strong>Data preservation.</strong> We make reasonable efforts to
          preserve your content (notes, conversations, preferences,
          attachments) across product changes. In rare cases, schema migrations
          during the beta period may require resets of derived state (cached
          summaries, embeddings, suggestion histories). Your authored content
          (notes you typed, files you uploaded, conversations you sent) is
          preserved through migrations except where explicitly disclosed in
          advance. We will provide reasonable advance notice if any planned
          change would affect authored content.
        </p>
        <p style={{ marginTop: "var(--space-3)" }}>
          <strong>Pricing.</strong> Beta-period pricing reflects current beta
          economics and may change when Fülkit transitions to general
          availability. Active beta subscribers will be given reasonable notice
          of any pricing change before it takes effect, and the right to
          cancel before the new pricing applies. Credit purchases (Ful credits
          and one-time top-ups) are honored at the price paid; only
          recurring-subscription pricing is subject to beta-to-GA repricing.
        </p>
        <p style={{ marginTop: "var(--space-3)" }}>
          <strong>Beta feedback.</strong> Bug reports, feature requests,
          product suggestions, and similar feedback you provide to Fülkit
          during the beta period are non-confidential. Fülkit may use, adopt,
          or implement such feedback without compensation or attribution to
          you. This does not apply to your authored content (notes,
          conversations, attachments), which remains your property under the
          "Your content and license to Fülkit" section below.
        </p>
        <p style={{ marginTop: "var(--space-3)" }}>
          <strong>End of beta.</strong> When Fülkit transitions to general
          availability, the Terms may be updated to reflect GA status. You may
          be asked to re-accept the updated Terms to continue using the
          service. If you decline, you will retain export and account-deletion
          access for at least 30 days after the GA transition.
        </p>
      </DocSection>

      <DocSection title="Where Fülkit operates">
        <p>
          Fülkit is offered to US residents. By using Fülkit, you confirm that
          you are a US resident, or you accept that Fülkit is not designed for
          users outside the United States and you use it at your own risk.
          Fülkit does not currently target users in the EU, UK, or other
          jurisdictions outside the US, and is not designed to comply with the
          GDPR or equivalent non-US frameworks. EU/UK users who choose to sign
          up acknowledge that Fülkit operates under US federal and US-state
          privacy laws only.
        </p>
      </DocSection>

      <DocSection title="Eligibility and age">
        <p>
          You must be at least 13 years old to use Fülkit. If you are between
          13 and the age of majority in your state, you must have parent or
          guardian consent. Fülkit is not directed at children under 13 and we
          do not knowingly accept accounts from anyone under 13.
        </p>
      </DocSection>

      <DocSection title="Your account">
        <p>
          You sign in with Google. You're responsible for your account activity
          and for keeping your sign-in credentials secure. One account per person.
          Don't share your access. Notify us at support@fulkit.app if you believe
          your account has been compromised.
        </p>
      </DocSection>

      <DocSection title="Subscriptions and billing">
        <p>
          {TIERS.standard.label} ({TIERS.standard.priceLabel}) and {TIERS.pro.label} ({TIERS.pro.priceLabel}) plans are billed monthly. Credits
          ({CREDITS.description}) are one-time purchases. New accounts include a
          14-day trial. Cancel anytime from Settings. No refunds are provided for
          partial billing periods. Refunds for full billing periods may be granted
          at Fülkit's discretion in cases of material service failure. Payment
          disputes and chargebacks are subject to Stripe's standard process.
        </p>
      </DocSection>

      <DocSection title="Referral program (Ful-Up)">
        <p>
          Each active referral earns you $1/mo off your subscription. At 25+
          referrals (Builder tier), excess credit converts to cash payouts via
          Stripe, subject to a $10 minimum. An "active" referral is someone who
          signed up through your link and maintains an active account. Fülkit
          reserves the right to modify the referral program, pause payouts, or
          revoke credits earned through abuse (self-referral, fake accounts, or
          incentivized signups).
        </p>
      </DocSection>

      <DocSection title="Your data">
        <p>
          You own your data. We store it to provide the service. You can export
          everything as markdown and delete your account at any time. See our{" "}
          <Link href="/privacy" style={{ color: "var(--color-text)", textDecoration: "underline" }}>
            Privacy Policy
          </Link>{" "}
          for details.
        </p>
      </DocSection>

      <DocSection title="Your content and license to Fülkit">
        <p>
          You retain ownership of all content you create or upload to Fülkit
          (notes, conversations, preferences, attachments). You grant Fülkit a
          limited, non-exclusive, non-sublicensable license to host, store,
          process, and display your content solely as needed to provide the
          service to you. This license terminates when you delete the content
          or close your account, except where retention is required by law or
          to resolve open billing/abuse matters.
        </p>
        <p style={{ marginTop: "var(--space-3)" }}>
          Fülkit retains all rights in the platform itself: software, design,
          trademarks, brand, documentation, and any AI prompts, system messages,
          or templates that ship with the product. Nothing in these Terms grants
          you ownership of Fülkit's intellectual property.
        </p>
        <p style={{ marginTop: "var(--space-3)" }}>
          Fülkit does not use your content to train AI models, ours or anyone
          else's, as stated in the Privacy Policy.
        </p>
      </DocSection>

      <DocSection title="AI limitations">
        <p>
          Fülkit uses AI that can make mistakes. Don't rely on it for medical,
          legal, or financial advice, or for any decision where accuracy is
          life-affecting. It's a thinking partner, not an authority. AI output
          may be incorrect, incomplete, or out of date; you are responsible for
          reviewing it before acting on it.
        </p>
      </DocSection>

      <DocSection title="Acceptable use">
        <p>
          Don't use Fülkit for anything illegal, harmful, or that violates
          others' rights. Specifically, you agree not to:
        </p>
        <ul style={{ paddingLeft: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
          <li>Use Fülkit to create or distribute unlawful, infringing, defamatory, fraudulent, or harassing content</li>
          <li>Attempt to extract, scrape, or replicate Fülkit's prompts, system messages, or model outputs at scale for competitive purposes</li>
          <li>Probe, scan, or test the vulnerability of Fülkit's systems without authorization</li>
          <li>Circumvent rate limits, billing, or access controls</li>
          <li>Resell, sublicense, or expose Fülkit's API capabilities to third parties without permission</li>
          <li>Upload content you don't have the right to upload</li>
        </ul>
        <p style={{ marginTop: "var(--space-3)" }}>
          Fülkit may suspend or terminate accounts that materially breach this
          section, with notice and a chance to cure where practicable.
        </p>
      </DocSection>

      <DocSection title="Termination">
        <p>
          You may cancel your account at any time from Settings. Cancellation
          takes effect at the end of your current billing period; no further
          charges will be made. After cancellation, your data remains accessible
          for export for 30 days, then is deleted per the Privacy Policy.
        </p>
        <p style={{ marginTop: "var(--space-3)" }}>
          Fülkit may suspend or terminate your account for material breach of
          these Terms (including the Acceptable Use section), with notice and a
          reasonable opportunity to cure where practicable. In cases of
          imminent harm, fraud, security threat, or legal compulsion, suspension
          may be immediate. On termination, the licenses you grant Fülkit and
          the licenses Fülkit grants you both end, except for sections that by
          their nature should survive (intellectual property, disclaimers,
          limitation of liability, indemnification, governing law).
        </p>
      </DocSection>

      <DocSection title="DMCA / copyright">
        <p>
          Fülkit complies with the Digital Millennium Copyright Act (17 USC
          §512). If you believe content on Fülkit infringes your copyright,
          send a notice to support@fulkit.app that includes: (1) your physical
          or electronic signature; (2) identification of the copyrighted work;
          (3) the location of the allegedly infringing material on Fülkit;
          (4) your contact information; (5) a good-faith statement that the
          use is unauthorized; and (6) a statement under penalty of perjury
          that the information is accurate and you are authorized to act for
          the rights holder. Counter-notifications follow the same address and
          the standard §512(g) process. Fülkit terminates the accounts of
          repeat infringers in appropriate circumstances.
        </p>
      </DocSection>

      <DocSection title="Disclaimers">
        <p>
          Fülkit is provided <strong>as is</strong> and <strong>as available</strong>,
          without warranties of any kind, express or implied, including but not
          limited to warranties of merchantability, fitness for a particular
          purpose, non-infringement, accuracy, or uninterrupted operation. To
          the maximum extent permitted by applicable law, Fülkit disclaims all
          such warranties. Some jurisdictions do not allow the disclaimer of
          implied warranties; in those jurisdictions, the foregoing disclaimers
          apply to the maximum extent permitted by law.
        </p>
      </DocSection>

      <DocSection title="Limitation of liability">
        <p>
          To the maximum extent permitted by applicable law, Fülkit (and its
          owners, employees, affiliates, and service providers) shall not be
          liable for any indirect, incidental, special, consequential, or
          punitive damages, or any loss of profits, revenue, data, or goodwill,
          arising out of or related to your use of Fülkit, even if Fülkit has
          been advised of the possibility of such damages.
        </p>
        <p style={{ marginTop: "var(--space-3)" }}>
          Fülkit's aggregate liability for all claims relating to the service
          will not exceed the greater of (a) the amounts you paid Fülkit in the
          twelve (12) months immediately preceding the event giving rise to the
          claim, or (b) one hundred US dollars ($100). Some jurisdictions do
          not allow these limitations; in those jurisdictions, liability is
          limited to the maximum extent permitted by law.
        </p>
      </DocSection>

      <DocSection title="Indemnification">
        <p>
          You agree to defend, indemnify, and hold harmless Fülkit, its owners,
          employees, affiliates, and service providers from and against any
          claims, damages, liabilities, costs, and expenses (including
          reasonable attorneys' fees) arising from (a) your use of Fülkit,
          (b) your content, (c) your violation of these Terms, or (d) your
          violation of any third-party right. Fülkit will indemnify you, to the
          extent of fees you have paid Fülkit in the prior twelve months, for
          claims that the Fülkit platform itself (excluding your content and
          your use) directly infringes a third party's US intellectual property
          right, provided you notify Fülkit promptly and cooperate in the
          defense.
        </p>
      </DocSection>

      <DocSection title="Force majeure">
        <p>
          Neither party is liable for any failure or delay in performance
          caused by events beyond reasonable control, including acts of God,
          natural disasters, war, terrorism, pandemic, civil unrest, government
          action, internet or telecommunications failures, or failures of
          upstream service providers. The affected party will use reasonable
          efforts to resume performance as soon as practicable.
        </p>
      </DocSection>

      <DocSection title="Governing law and venue">
        <p>
          These Terms are governed by the laws of the State of Utah, without
          regard to its conflict-of-laws principles. Any dispute arising out of
          or related to Fülkit or these Terms that is not resolved informally
          shall be brought exclusively in the state or federal courts located
          in Salt Lake County, Utah, and you consent to personal jurisdiction
          and venue in those courts. Nothing in this section limits either
          party's right to seek injunctive or equitable relief in any court of
          competent jurisdiction.
        </p>
      </DocSection>

      <DocSection title="Changes to these Terms">
        <p>
          Fülkit may update these Terms from time to time. When changes are
          material, we will update the "Last updated" date above and, where
          appropriate, surface a notice in-product. Your continued use of
          Fülkit after material changes take effect constitutes acceptance of
          the updated Terms.
        </p>
      </DocSection>

      <DocSection title="Miscellaneous">
        <p>
          <strong>Severability.</strong> If any provision of these Terms is
          held unenforceable, the remaining provisions remain in full force.
        </p>
        <p style={{ marginTop: "var(--space-3)" }}>
          <strong>Entire agreement.</strong> These Terms together with the
          Privacy Policy constitute the entire agreement between you and
          Fülkit and supersede any prior agreements on the same subject.
        </p>
        <p style={{ marginTop: "var(--space-3)" }}>
          <strong>No waiver.</strong> Fülkit's failure to enforce any provision
          is not a waiver of its right to do so later.
        </p>
        <p style={{ marginTop: "var(--space-3)" }}>
          <strong>Assignment.</strong> You may not assign these Terms without
          Fülkit's prior written consent. Fülkit may assign these Terms in
          connection with a merger, acquisition, sale of assets, or by operation
          of law.
        </p>
      </DocSection>

      <DocSection title="Contact">
        <p>
          Questions about Fülkit? Email{" "}
          <a
            href="mailto:support@fulkit.app"
            style={{
              color: "var(--color-text)",
              fontWeight: "var(--font-weight-semibold)",
              textDecoration: "underline",
            }}
          >
            support@fulkit.app
          </a>
          .
        </p>
      </DocSection>
    </DocLayout>
  );
}
